import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../../../../environments/environment';
import { DashboardService } from '../../../services/dashboard.service';
import {
  AnalyticsPeriod,
  AnalyticsTab,
  ProfessorAnalyticsBundle,
  OverviewTheme,
  OverviewQuestion,
  MediaConsumptionRow,
  TangramMetrics,
  MyTangramRow,
  ClassroomMetrics,
  EngagementMetrics,
  TrendDelta
} from '../models/analytics.types';
import { PROFESSOR_ANALYTICS_MOCK_BUNDLE } from './professor-analytics-mock-data.const';

// Criteria that drive the Top Performers / Needs Attention lists.
// Server-computed and echoed back so the UI can render the "needs
// at least N views" explanation without hard-coding the threshold.
export interface OverviewListCriteria {
  minViewsForRanking: number;
  topCompletionThreshold: number;
  attentionCompletionThreshold: number;
  listSize: number;
}

// Backend payload returned by `GET /api/v1/professor-analytics/overview`.
// Wire-format strings (`'this-month'`) differ from the UI labels
// (`'This Month'`) — see `periodToWire` below for the mapping.
export interface OverviewAnalyticsResponse {
  tangramMetrics: TangramMetrics;
  // Headline KPI count for the "Published Tangrams" tile. Counts every
  // non-draft, non-deleted pathway the professor owns — same definition
  // as the My Tangrams "Published" chip.
  //
  // The `/overview` endpoint deliberately omits the full `myTangrams[]`
  // array — that list is only rendered on the Tangram Metrics tab and
  // would add tens of KB to the Overview response on accounts with many
  // published pathways.
  publishedTangramsCount: number;
  // Server-computed per the criteria below — already filtered + sorted.
  topPerformers: MyTangramRow[];
  needsAttention: MyTangramRow[];
  trends: { [key: string]: TrendDelta };
  criteria: OverviewListCriteria;
  period: string;
}

// Criteria the Tangram Metrics tab uses to pick top/low rankings. Echoed
// back from the server so empty-state copy can reference the live
// thresholds without hard-coding them.
export interface TangramMetricsListCriteria {
  minViewsForRanking: number;
  listSize: number;
}

// Backend payload returned by `GET /api/v1/professor-analytics/tangram-metrics`.
// Mirrors the Overview response shape but with simpler (non-threshold)
// top/low rankings — see backend `buildTangramMetricsRankings` for the rule.
export interface TangramMetricsAnalyticsResponse {
  tangramMetrics: TangramMetrics;
  // Same headline KPI as the Overview tab — every non-draft, non-deleted
  // pathway. The KPI tile binds to this; the per-row table reads
  // `myTangrams[]` (eligible-only) so empty pathways don't dilute averages.
  publishedTangramsCount: number;
  // Server-paginated. `myTangrams` is a page slice; `myTangramsTotal` is
  // the full count needed to drive the pagination control. Top/low
  // rankings are still computed against the full list server-side, so
  // they don't shift as the user pages through.
  myTangrams: MyTangramRow[];
  myTangramsTotal: number;
  page: number;
  pageSize: number;
  topTangrams: MyTangramRow[];
  lowTangrams: MyTangramRow[];
  trends: { [key: string]: TrendDelta };
  criteria: TangramMetricsListCriteria;
  period: string;
}

// ── Content Metrics tab ──────────────────────────────────────────────
// The Content Metrics tab is per-media (not per-tangram). The dropdown
// filter at the top of the tab can scope the response to a single
// tangram so the professor can drill into one tangram's media without
// leaving the tab.
export interface ContentMetricsTangramOption {
  pathwayId: string;
  title: string;
  // Number of (non-deleted) media items this tangram contains. Lets the
  // dropdown render "Algebra Basics (12 media)" without an extra query.
  mediaCount: number;
}

export interface ContentMetricsRow {
  videoId: string;
  title: string;
  // Canonical lower-case media-type token — same vocabulary the Tangram
  // Metrics tab uses so chip styling stays consistent.
  type: 'video' | 'audio' | 'pdf' | 'image' | 'document' | 'article';
  pathwayId: string;
  pathwayTitle: string;
  views: number;
  completionRate: number;       // integer 0–100
  avgTimeSpent: string;         // formatted, e.g. "12m"
  avgTimeSpentSeconds: number;  // raw seconds for client-side sorts
  rating: number;               // 0 if no ratings; otherwise 1.0–5.0 (1 dp)
  interactions: number;         // comment count in the period
  dropOffPoint: string;         // coarse band — see backend `dropOffBand`
}

export interface ContentMetricsTypeRollup {
  type: string;                 // pretty label, e.g. "Video"
  typeKey: string;              // canonical lower-case token
  mediaCount: number;
  totalViews: number;
  avgCompletionRate: number;    // integer 0–100
  avgRating: number;            // 0 if no ratings; otherwise 1.0–5.0 (1 dp)
}

export interface ContentMetricsSummary {
  totalMedia: number;
  totalViews: number;
  avgCompletionRate: number;
  avgRating: number;
}

export interface ContentMetricsListCriteria {
  minViewsForRanking: number;
  topCompletionThreshold: number;
  lowCompletionThreshold: number;
  listSize: number;
  /** Max grouped question themes returned for the active tangram filter. */
  topQuestionsLimit?: number;
  /** Distinct grouped themes before the limit slice. */
  topQuestionsGroupedTotal?: number;
  /** Raw AI-chat question documents in the period (before grouping). */
  topQuestionsRawTotal?: number;
}

export interface ContentMetricsAnalyticsResponse {
  // Always the full list of the professor's eligible tangrams so the
  // dropdown can keep showing every option even when one is selected.
  tangrams: ContentMetricsTangramOption[];
  // Server-paginated. `contentMetrics` is the page slice (sorted views
  // desc); `mediaEngagement` is the same dataset filtered to rows with
  // views > 0 (drives the "Time Spent & Drop-off" table) — also a page
  // slice. Totals come back alongside so the UI can drive the
  // pagination strip without re-deriving it client-side.
  contentMetrics: ContentMetricsRow[];
  contentMetricsTotal: number;
  mediaEngagement: ContentMetricsRow[];
  mediaEngagementTotal: number;
  page: number;
  pageSize: number;
  mediaTypePerformance: ContentMetricsTypeRollup[];
  topPerformingContent: ContentMetricsRow[];
  lowPerformingContent: ContentMetricsRow[];
  summary: ContentMetricsSummary;
  // Grouped AI-chat question themes (limit depends on tangram filter).
  topQuestions: OverviewQuestion[];
  // Echo of the resolved filter — empty string means "all tangrams".
  filter: { pathwayId: string };
  criteria: ContentMetricsListCriteria;
  period: string;
}

// ── Viewer Metrics (Educator View) tab ───────────────────────────
// Per-student rollups, classroom-level KPIs, and comment-derived theme /
// question callouts for the professor's eligible tangrams. The optional
// `pathwayId` filter scopes every metric to one tangram so the professor
// can drill into a single class — the `tangrams[]` array always carries
// the full list of eligible options for the dropdown.
export interface ViewerMetricsTangramOption {
  pathwayId: string;
  title: string;
  mediaCount: number;
}

export interface ViewerMetricsMediaTime {
  videoId: string;
  pathwayId: string;
  title: string;
  timeSpent: string;              // formatted e.g. "4m 12s"
  timeSpentSec: number;           // raw seconds for sorting / sums
}

export interface ViewerMetricsUserRow {
  userId: string;
  user: string;
  tangramsViewed: number;
  avgTimePerTangram: string;     // e.g. "12m 30s"
  totalTimeOnTangram?: string;   // total time this learner spent on the scoped Tangram(s)
  totalTimeOnTangramSec?: number;
  completionRate: number;         // integer 0–100
  commentsAdded: number;
  pollsAnswered: number;          // sum of poll votes + engagement-question answers
  ratingsGiven: number;           // media + pathway ratings, summed
  aiChatSessions: number;         // 0 today (chat sessions are not persisted)
  mediaBreakdown?: ViewerMetricsMediaTime[]; // per-media time spent for this learner
}

export interface ViewerMetricsAnalyticsResponse {
  tangrams: ViewerMetricsTangramOption[];
  classroomMetrics: ClassroomMetrics;
  engagementMetrics: EngagementMetrics;
  userEngagement: ViewerMetricsUserRow[];
  topThemes: OverviewTheme[];
  questionInsights: OverviewQuestion[];
  trends: { [key: string]: TrendDelta };
  filter: { pathwayId: string };
  period: string;
}

// ── Event Tracking tab ───────────────────────────────────────────────
// One row per event we track. `count` is the integer occurrence count
// for the period (or `null` for events that only have an average value
// — time/rate metrics). `avgValue` is a pre-formatted string the table
// renders verbatim. `lastTriggered` is a relative date string like
// "Today", "Yesterday", "3 days ago" (or an ISO date for older events
// / "all-time" period).
export interface EventTrackingTangramOption {
  pathwayId: string;
  title: string;
  mediaCount: number;
}

export interface EventTrackingLogRowDto {
  event: string;
  count: number | null;
  uniqueUsers: number;
  lastTriggered: string;
  avgValue?: string;
}

export interface EventTrackingSummary {
  totalEvents: number;
  totalUniqueUsers: number;
  eventsTracked: number;
}

export interface EventTrackingAnalyticsResponse {
  tangrams: EventTrackingTangramOption[];
  eventTrackingLog: EventTrackingLogRowDto[];
  summary: EventTrackingSummary;
  filter: { pathwayId: string };
  period: string;
}

// ── AI Insights tab ────────────────────────────────────────────────────
// One row per AI-derived observation. Categories, impact bands, and icons
// are decided server-side so the FE just renders what it gets — adding a
// new insight category is a backend-only change.
export interface AiInsightTangramOption {
  pathwayId: string;
  title: string;
  mediaCount: number;
}

export interface AiInsightRow {
  rank: number;
  category: string;          // 'Top Tangram' | 'Drop-off Pattern' | 'Engagement Gap' | 'Top Discussion' | 'Participation Peak' | …
  insight: string;           // human-readable narrative — already filled with concrete numbers
  impact: 'high' | 'medium' | 'low';
  icon: string;              // icon hint — see component for the rendered SVG
}

export interface AiInsightSummary {
  eligibleTangrams: number;
  totalViews: number;
  totalUniqueStudents: number;
}

export interface AiInsightCriteria {
  minViewsForTangram: number;
  minViewsForDiscussion: number;
  newTangramCount: number;
  dropOffCompletionThreshold: number;
}

export type AiInsightProvider = 'deterministic' | 'claude' | 'gemini';

// When the server transparently swaps a single-Tangram request back to
// catalogue mode (because the chosen Tangram has no engagement signal),
// it reports the swap in this field so the FE can reset the dropdown
// and show the user a small "showing all Tangrams instead" notice.
// `null` means no fallback fired.
export interface AiInsightsAutoFallback {
  reason: 'no-signal' | 'no-period-activity';
  attemptedPathwayId: string;
  attemptedTangramTitle: string;
}

export interface AiInsightsAnalyticsResponse {
  tangrams: AiInsightTangramOption[];
  insights: AiInsightRow[];
  summary: AiInsightSummary;
  generatedAt: string;        // ISO timestamp the insights were derived at
  // Provider that produced the rendered narratives. Echoes back what the
  // FE sent as the `provider` query param (or 'deterministic' when omitted).
  provider: AiInsightProvider;
  // Provider tokens this server build supports — lets the FE render the
  // button row off a single source of truth instead of hard-coding it.
  availableProviders: AiInsightProvider[];
  criteria: AiInsightCriteria;
  filter: { pathwayId: string };
  autoFallback: AiInsightsAutoFallback | null;
  period: string;
}

// ── Reports & Export tab ─────────────────────────────────────────────
// Two endpoints back this tab:
//   GET  ...                       → schedule cards + share-summary KPIs
//   PATCH ...//schedule            → upsert per-frequency enable/disable
//
// `reportSchedules[]` always carries all three canonical frequencies
// (Daily / Weekly / Monthly) — when no preference row exists for the
// professor the backend fills in a sensible default. `recipients` and
// `nextSend` are derived live (editor invitee count, period activity,
// "next Monday at 8am" etc.) so the cards never go stale relative to
// the catalogue.

export type ReportScheduleFrequency = 'Daily' | 'Weekly' | 'Monthly';

export interface ReportScheduleRow {
  frequency: ReportScheduleFrequency;
  nextSend: string;       // pre-formatted (e.g. "Tomorrow, 8:00 AM")
  recipients: number;
  enabled: boolean;
}

// Pre-formatted snapshot rendered by the Share Report panel and dropped
// verbatim into the email subject/body the "Share Report" button opens.
export interface ReportShareSummary {
  reportTitle: string;
  periodLabel: string;
  publishedTangrams: number;
  totalViews: number;
  uniqueStudents: number;
  avgCompletionRate: number;
  avgTimeSpent: string;
  generatedAt: string;
  shareSubject: string;
  shareBody: string;
}

export interface ReportsExportAnalyticsResponse {
  reportSchedules: ReportScheduleRow[];
  shareSummary: ReportShareSummary;
  availableFrequencies: ReportScheduleFrequency[];
  period: string;
}

// Single source of truth for analytics data fetching.
//
// Today: derives Overview rows from the existing pathway/comments
// endpoints and falls back to a static demo bundle for everything
// else. The contract below is what the backend should implement
// (see API_CONTRACT.md) — only `fetchBundle()` should change when
// real endpoints land; tabs stay untouched.
@Injectable({ providedIn: 'root' })
export class ProfessorAnalyticsService {
  private readonly baseUrl = `${environment.baseUrl}/professor-analytics`;

  // ── Per-tab data cache for the dashboard-level Export button ────────
  //
  // Each tab pushes its latest server payload here when its `load()`
  // resolves. The parent dashboard reads from the cache when the user
  // clicks the top-right Export button so the CSV always reflects the
  // active tab's currently-rendered data — no extra fetch, no stale
  // mock fallback.
  //
  // The cache is intentionally keyed by `AnalyticsTab` (not by period or
  // pathway filter) because the tabs themselves already refresh on those
  // changes; the cache just mirrors whatever the tab currently shows.
  // It's NOT intended as a general fetch cache — `getXyzAnalytics()`
  // still hits the backend on every call.
  private latestTabData = new Map<AnalyticsTab, unknown>();

  constructor(
    private dashboardService: DashboardService,
    private http: HttpClient,
  ) {}

  // Tabs call this from inside their successful load handler. The shape
  // is intentionally opaque (`unknown`) — the parent dashboard knows the
  // shape per `AnalyticsTab` value when it builds the CSV.
  setLatestTabData(tab: AnalyticsTab, data: unknown): void {
    this.latestTabData.set(tab, data);
  }

  getLatestTabData<T = unknown>(tab: AnalyticsTab): T | undefined {
    return this.latestTabData.get(tab) as T | undefined;
  }

  hasLatestTabData(tab: AnalyticsTab): boolean {
    return this.latestTabData.has(tab);
  }

  // Optional cache invalidation hook — not currently used but exposed so
  // callers can drop the cache when the user navigates away from the
  // dashboard.
  clearLatestTabData(): void {
    this.latestTabData.clear();
  }

  // ────────────────────────────────────────────────────────────────
  // Overview tab — real backend (replaces the static mock for KPIs,
  // top/low tangrams, and trend deltas).
  // ────────────────────────────────────────────────────────────────

  // Translate UI-facing period labels into the wire format the backend
  // expects (`this-week`, `this-month`, `this-term`, `all-time`, `custom`).
  private periodToWire(period: AnalyticsPeriod): string {
    if (period === 'Custom Range') { return 'custom'; }
    return String(period || '').toLowerCase().replace(/\s+/g, '-');
  }

  private rangeParams(period: AnalyticsPeriod, startDate = '', endDate = ''): Record<string, string> {
    const p: Record<string, string> = { period: this.periodToWire(period) };
    if (period === 'Custom Range' && startDate && endDate) {
      p.startDate = startDate;
      p.endDate   = endDate;
    }
    return p;
  }

  getOverviewAnalytics(period: AnalyticsPeriod, startDate = '', endDate = ''): Observable<OverviewAnalyticsResponse> {
    const _wire = this.periodToWire(period);
    return this.http
      .get<{ success: boolean; data: OverviewAnalyticsResponse }>(
        `${this.baseUrl}/overview`,
        { params: this.rangeParams(period, startDate, endDate) },
      )
      .pipe(map((res) => (res && res.data) ? res.data : ({
        tangramMetrics: { views: 0, uniqueUsers: 0, completionRate: 0, avgTimeSpent: '0m' },
        publishedTangramsCount: 0,
        topPerformers: [],
        needsAttention: [],
        trends: {},
        criteria: {
          minViewsForRanking: 5,
          topCompletionThreshold: 70,
          attentionCompletionThreshold: 40,
          listSize: 3,
        },
        period: this.periodToWire(period),
      } as OverviewAnalyticsResponse)));
  }

  // ────────────────────────────────────────────────────────────────
  // Tangram Metrics tab — real backend (replaces the static mock for
  // KPIs, my-tangrams list, top/low rankings, and trend deltas).
  // ────────────────────────────────────────────────────────────────

  getTangramMetricsAnalytics(
    period: AnalyticsPeriod,
    startDate = '',
    endDate = '',
    page = 1,
    pageSize = 5,
  ): Observable<TangramMetricsAnalyticsResponse> {
    const _wire = this.periodToWire(period);
    const params: { [k: string]: string } = this.rangeParams(period, startDate, endDate);
    params.page = String(page);
    params.pageSize = String(pageSize);
    return this.http
      .get<{ success: boolean; data: TangramMetricsAnalyticsResponse }>(
        `${this.baseUrl}/tangram-metrics`,
        { params },
      )
      .pipe(map((res) => (res && res.data) ? res.data : ({
        tangramMetrics: { views: 0, uniqueUsers: 0, completionRate: 0, avgTimeSpent: '0m' },
        publishedTangramsCount: 0,
        myTangrams: [],
        myTangramsTotal: 0,
        page,
        pageSize,
        topTangrams: [],
        lowTangrams: [],
        trends: {},
        criteria: { minViewsForRanking: 5, listSize: 5 },
        period: this.periodToWire(period),
      } as TangramMetricsAnalyticsResponse)));
  }

  // ────────────────────────────────────────────────────────────────
  // Content Metrics tab — real backend (per-media rows + per-type
  // rollup + top/low callouts). Optional `pathwayId` scopes every
  // metric on the response to a single tangram.
  // ────────────────────────────────────────────────────────────────

  getContentMetricsAnalytics(
    period: AnalyticsPeriod,
    pathwayId: string = '',
    startDate = '',
    endDate = '',
    page = 1,
    pageSize = 5,
  ): Observable<ContentMetricsAnalyticsResponse> {
    const params: { [key: string]: string } = this.rangeParams(period, startDate, endDate);
    if (pathwayId) {
      params.pathwayId = pathwayId;
    }
    params.page = String(page);
    params.pageSize = String(pageSize);
    return this.http
      .get<{ success: boolean; data: ContentMetricsAnalyticsResponse }>(
        `${this.baseUrl}/content-metrics`,
        { params },
      )
      .pipe(map((res) => (res && res.data) ? res.data : ({
        tangrams: [],
        contentMetrics: [],
        contentMetricsTotal: 0,
        mediaEngagement: [],
        mediaEngagementTotal: 0,
        page,
        pageSize,
        mediaTypePerformance: [],
        topPerformingContent: [],
        lowPerformingContent: [],
        summary: { totalMedia: 0, totalViews: 0, avgCompletionRate: 0, avgRating: 0 },
        filter: { pathwayId: pathwayId || '' },
        criteria: {
          minViewsForRanking: 3,
          topCompletionThreshold: 70,
          lowCompletionThreshold: 40,
          listSize: 5,
        },
        period: this.periodToWire(period),
      } as ContentMetricsAnalyticsResponse)));
  }

  // ────────────────────────────────────────────────────────────────
  // Viewer Metrics tab — real backend (per-student rollups,
  // classroom + engagement KPIs, comment-derived theme/question
  // callouts). Optional `pathwayId` scopes every metric to a single
  // tangram.
  // ────────────────────────────────────────────────────────────────

  getViewerMetricsAnalytics(
    period: AnalyticsPeriod,
    pathwayId: string = '',
    startDate = '',
    endDate = '',
  ): Observable<ViewerMetricsAnalyticsResponse> {
    const params: { [key: string]: string } = this.rangeParams(period, startDate, endDate);
    if (pathwayId) {
      params.pathwayId = pathwayId;
    }
    return this.http
      .get<{ success: boolean; data: ViewerMetricsAnalyticsResponse }>(
        `${this.baseUrl}/viewer-metrics`,
        { params },
      )
      .pipe(map((res) => (res && res.data) ? res.data : ({
        tangrams: [],
        classroomMetrics: { activeStudentsPct: 0, avgCompletionPerTangram: 0, avgTimePerTangram: '0m', avgTimePerContent: '0m' },
        engagementMetrics: { commentsPerTangram: 0, pollCompletionRate: 0, questionCompletionRate: 0 },
        userEngagement: [],
        topThemes: [],
        questionInsights: [],
        trends: {},
        filter: { pathwayId: pathwayId || '' },
        period: this.periodToWire(period),
      } as ViewerMetricsAnalyticsResponse)));
  }

  // ────────────────────────────────────────────────────────────────
  // Event Tracking tab — real backend (per-event log rows + dropdown
  // options + summary tiles). Optional `pathwayId` scopes every row
  // to a single tangram.
  // ────────────────────────────────────────────────────────────────

  getEventTrackingAnalytics(
    period: AnalyticsPeriod,
    pathwayId: string = '',
    startDate = '',
    endDate = '',
  ): Observable<EventTrackingAnalyticsResponse> {
    const params: { [key: string]: string } = this.rangeParams(period, startDate, endDate);
    if (pathwayId) {
      params.pathwayId = pathwayId;
    }
    return this.http
      .get<{ success: boolean; data: EventTrackingAnalyticsResponse }>(
        `${this.baseUrl}/event-tracking`,
        { params },
      )
      .pipe(map((res) => (res && res.data) ? res.data : ({
        tangrams: [],
        eventTrackingLog: [],
        summary: { totalEvents: 0, totalUniqueUsers: 0, eventsTracked: 0 },
        filter: { pathwayId: pathwayId || '' },
        period: this.periodToWire(period),
      } as EventTrackingAnalyticsResponse)));
  }

  // ────────────────────────────────────────────────────────────────
  // AI Insights tab — real backend (deterministic insights computed
  // from the same data the other tabs render). Optional `pathwayId`
  // scopes every insight to one tangram.
  // ────────────────────────────────────────────────────────────────

  getAiInsightsAnalytics(
    period: AnalyticsPeriod,
    pathwayId: string = '',
    provider: AiInsightProvider = 'deterministic',
    startDate = '',
    endDate = '',
  ): Observable<AiInsightsAnalyticsResponse> {
    const params: { [key: string]: string } = this.rangeParams(period, startDate, endDate);
    if (pathwayId) {
      params.pathwayId = pathwayId;
    }
    if (provider && provider !== 'deterministic') {
      params.provider = provider;
    }
    return this.http
      .get<{ success: boolean; data: AiInsightsAnalyticsResponse }>(
        `${this.baseUrl}/ai-insights`,
        { params },
      )
      .pipe(map((res) => (res && res.data) ? res.data : ({
        tangrams: [],
        insights: [],
        summary: { eligibleTangrams: 0, totalViews: 0, totalUniqueStudents: 0 },
        generatedAt: new Date().toISOString(),
        provider,
        availableProviders: ['deterministic', 'claude', 'gemini'],
        criteria: {
          minViewsForTangram: 5,
          minViewsForDiscussion: 5,
          newTangramCount: 3,
          dropOffCompletionThreshold: 40,
        },
        filter: { pathwayId: pathwayId || '' },
        autoFallback: null,
        period: this.periodToWire(period),
      } as AiInsightsAnalyticsResponse)));
  }

  // ────────────────────────────────────────────────────────────────
  // Reports & Export tab — real backend (schedule cards + share
  // summary). Toggle persistence rides on a sibling PATCH endpoint.
  // ────────────────────────────────────────────────────────────────

  getReportsExportAnalytics(period: AnalyticsPeriod, startDate = '', endDate = ''): Observable<ReportsExportAnalyticsResponse> {
    return this.http
      .get<{ success: boolean; data: ReportsExportAnalyticsResponse }>(
        `${this.baseUrl}/reports-export`,
        { params: this.rangeParams(period, startDate, endDate) },
      )
      .pipe(map((res) => (res && res.data) ? res.data : ({
        reportSchedules: [],
        shareSummary: {
          reportTitle: 'Tangram Analytics',
          periodLabel: String(period),
          publishedTangrams: 0,
          totalViews: 0,
          uniqueStudents: 0,
          avgCompletionRate: 0,
          avgTimeSpent: '0m',
          generatedAt: new Date().toISOString(),
          shareSubject: 'Tangram Analytics',
          shareBody: 'No data available.',
        },
        availableFrequencies: ['Daily', 'Weekly', 'Monthly'],
        period: this.periodToWire(period),
      } as ReportsExportAnalyticsResponse)));
  }

  updateReportSchedule(frequency: ReportScheduleFrequency, enabled: boolean):
    Observable<{ frequency: ReportScheduleFrequency; enabled: boolean }> {
    return this.http
      .patch<{ success: boolean; data: { frequency: ReportScheduleFrequency; enabled: boolean } }>(
        `${this.baseUrl}/reports-export/schedule`,
        { frequency, enabled },
      )
      .pipe(map((res) => (res && res.data) ? res.data : { frequency, enabled }));
  }

  // ────────────────────────────────────────────────────────────────
  // Public API — what tabs and the parent orchestrator consume.
  // ────────────────────────────────────────────────────────────────

  fetchBundle(pathwayId: string, _period: AnalyticsPeriod): Observable<ProfessorAnalyticsBundle> {
    if (!pathwayId || pathwayId === 'local-pathway') {
      return of(this.buildMockBundle());
    }
    return new Observable<ProfessorAnalyticsBundle>((subscriber) => {
      this.dashboardService.getPathwayDetail(pathwayId).subscribe(
        (response: any) => {
          const pathway = (response && response.data) || null;
          if (!pathway) {
            subscriber.next(this.buildMockBundle());
            subscriber.complete();
            return;
          }
          this.buildBundleFromPathway(pathway, pathwayId).subscribe(
            (bundle) => { subscriber.next(bundle); subscriber.complete(); },
            ()       => { subscriber.next(this.buildMockBundle()); subscriber.complete(); }
          );
        },
        () => { subscriber.next(this.buildMockBundle()); subscriber.complete(); }
      );
    });
  }

  // Mock helper exposed so the parent component can fall back without
  // re-importing the constant.
  buildMockBundle(): ProfessorAnalyticsBundle {
    return JSON.parse(JSON.stringify(PROFESSOR_ANALYTICS_MOCK_BUNDLE));
  }

  // ────────────────────────────────────────────────────────────────
  // Authorization helper preserved from original component.
  // ────────────────────────────────────────────────────────────────

  canViewProfessorAnalytics(pathway: any, currentUser: any): boolean {
    if (!pathway) { return false; }
    if (!currentUser) { return true; }
    const role = String(currentUser.role || '').toUpperCase();
    if (role === 'SUPER_USER' || role === 'CURATOR' || role === 'PROFESSOR') { return true; }
    return currentUser._id === pathway.createdBy;
  }

  // ────────────────────────────────────────────────────────────────
  // Pathway-driven Overview derivation (preserves existing behavior).
  // Public so the parent component can run the auth check before
  // building the bundle.
  // ────────────────────────────────────────────────────────────────

  buildBundleFromPathway(pathway: any, pathwayId: string): Observable<ProfessorAnalyticsBundle> {
    const mediaList = Array.isArray(pathway && pathway.playlists) ? pathway.playlists : [];
    if (!mediaList.length) {
      return of(this.buildMockBundle());
    }

    const requests = mediaList.map((media: any) => {
      const videoId = media && media._id ? media._id : '';
      if (!videoId) { return of({ videoId: '', media, comments: [] as any[] }); }
      return this.dashboardService.getVideoCommentsList(
        { videoId, type: 'all', page: 1, limit: 100, isFlagged: false, pathwayId, timeSlot: 0 },
        'all',
        'private'
      ).pipe(
        map((res: any) => ({ videoId, media, comments: (res && res.data) || [] })),
        catchError(() => of({ videoId, media, comments: [] as any[] }))
      );
    });

    return forkJoin(requests).pipe(
      map((commentRecords: any[]) => {
        const students = this.collectStudentsFromComments(commentRecords);
        const mediaRows = this.buildMediaConsumptionRows(students, commentRecords);
        const topThemes = this.extractTopThemes(commentRecords, pathway);
        const questionInsights = this.buildQuestionInsights(commentRecords);

        // Take the rest from mock for now; per-tab endpoints will
        // replace these slices in API_CONTRACT.md Phase 3.
        const base = this.buildMockBundle();
        return {
          ...base,
          mediaRows,
          topThemes: topThemes.length ? topThemes : base.topThemes,
          questionInsights: questionInsights.length ? questionInsights : base.questionInsights
        };
      })
    );
  }

  // ────────────────────────────────────────────────────────────────
  // Comment-derived helpers (carried over from original component).
  // ────────────────────────────────────────────────────────────────

  private collectStudentsFromComments(commentRecords: any[]): any[] {
    const userMap: { [k: string]: any } = {};
    (commentRecords || []).forEach((record: any) => {
      (record.comments || []).forEach((comment: any) => {
        const user = comment && (comment.user || comment.userId) ? (comment.user || comment.userId) : null;
        const id = user && user._id ? user._id : '';
        if (!id || userMap[id]) { return; }
        userMap[id] = {
          _id: id,
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          screenName: user.screenName || user.name || 'User'
        };
      });
    });
    return Object.keys(userMap).map((k) => userMap[k]);
  }

  private buildMediaConsumptionRows(students: any[], commentRecords: any[]): MediaConsumptionRow[] {
    const rows: MediaConsumptionRow[] = [];
    (commentRecords || []).forEach((record: any) => {
      const media = record.media || {};
      const comments = record.comments || [];
      const mediaType = this.resolveMediaType(media);
      const countByUser: { [k: string]: number } = {};
      comments.forEach((c: any) => {
        const owner = (c && c.user && c.user._id) || c.userId || '';
        if (owner) { countByUser[owner] = (countByUser[owner] || 0) + 1; }
      });
      (students || []).forEach((student: any) => {
        const id = student && student._id ? student._id : '';
        const count = id ? (countByUser[id] || 0) : 0;
        rows.push({
          mediaId: media._id,
          mediaTitle: media.title || 'Untitled media',
          mediaType,
          studentName:
            `${student.firstName || ''} ${student.lastName || ''}`.trim() ||
            student.screenName ||
            'User',
          engaged: count > 0,
          commentCount: count
        });
      });
    });
    return rows;
  }

  private resolveMediaType(media: any): string {
    const platform = String(media && media.platForm ? media.platForm : '').toLowerCase();
    const tagName = String(media && media.tag && media.tag.name ? media.tag.name : '').toLowerCase();
    const videoUrl = String(media && media.videoURL ? media.videoURL : '').toLowerCase();
    if (platform === 'youtube') { return 'video'; }
    if (tagName.indexOf('pdf') > -1 || videoUrl.indexOf('.pdf') > -1) { return 'pdf'; }
    return 'article';
  }

  private extractTopThemes(commentRecords: any[], pathway: any): OverviewTheme[] {
    const baseText: string[] = [];
    if (pathway && pathway.description) { baseText.push(pathway.description); }
    ((pathway && pathway.upskills) || []).forEach((up: any) => {
      if (up && up.title) { baseText.push(up.title); }
    });
    commentRecords.forEach((record: any) => {
      (record.comments || []).forEach((c: any) => {
        if (c && c.text) { baseText.push(c.text); }
      });
    });

    const stopWords: { [k: string]: boolean } = {
      the: true, and: true, for: true, that: true, this: true, with: true, from: true,
      are: true, was: true, were: true, your: true, about: true, what: true, when: true,
      how: true, into: true, just: true, more: true, can: true, could: true, should: true, would: true
    };
    const tokenCounts: { [k: string]: number } = {};
    const bigramCounts: { [k: string]: number } = {};

    baseText.forEach((text: string) => {
      const tokens = String(text || '')
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .split(/\s+/)
        .filter((t: string) => t.length > 3 && !stopWords[t]);
      tokens.forEach((t: string) => { tokenCounts[t] = (tokenCounts[t] || 0) + 1; });
      for (let i = 0; i < tokens.length - 1; i++) {
        const bg = `${tokens[i]} ${tokens[i + 1]}`;
        bigramCounts[bg] = (bigramCounts[bg] || 0) + 1;
      }
    });

    const rankedBigrams = Object.keys(bigramCounts)
      .map((k) => ({ key: k, value: bigramCounts[k] }))
      .sort((a, b) => b.value - a.value);
    const rankedTokens = Object.keys(tokenCounts)
      .map((k) => ({ key: k, value: tokenCounts[k] }))
      .sort((a, b) => b.value - a.value);

    const themes: OverviewTheme[] = [];
    rankedBigrams.slice(0, 3).forEach((row) => {
      themes.push({
        name: this.titleCase(row.key),
        evidence: row.value,
        description: `Viewers repeatedly discuss ${row.key} across course interactions.`
      });
    });
    if (themes.length < 3) {
      rankedTokens.forEach((row) => {
        if (themes.length >= 3) { return; }
        if (!themes.some((t) => t.name.toLowerCase().indexOf(row.key) > -1)) {
          themes.push({
            name: this.titleCase(row.key),
            evidence: row.value,
            description: `${this.titleCase(row.key)} appears frequently in discussions.`
          });
        }
      });
    }
    return themes.slice(0, 3);
  }

  private buildQuestionInsights(commentRecords: any[]): OverviewQuestion[] {
    const questionTexts: string[] = [];
    commentRecords.forEach((record: any) => {
      (record.comments || []).forEach((c: any) => {
        const text = String(c && c.text ? c.text : '').trim();
        const isQ = String(c && c.type ? c.type : '').toUpperCase() === 'QUESTION';
        if (text && (isQ || text.indexOf('?') > -1)) { questionTexts.push(text); }
      });
    });
    return this.aggregateQuestionTopics(questionTexts);
  }

  private aggregateQuestionTopics(questionTexts: string[]): OverviewQuestion[] {
    const stopWords: { [k: string]: boolean } = {
      the: true, and: true, what: true, why: true, when: true, where: true,
      how: true, can: true, does: true, with: true, from: true, this: true
    };
    const grouped: { [k: string]: OverviewQuestion } = {};
    (questionTexts || []).forEach((q) => {
      const key = String(q || '')
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .split(/\s+/)
        .filter((w: string) => w.length > 3 && !stopWords[w])
        .slice(0, 5)
        .join(' ');
      if (!key) { return; }
      if (!grouped[key]) {
        grouped[key] = {
          topic: this.titleCase(key.split(' ').slice(0, 3).join(' ')),
          sample: q,
          count: 0
        };
      }
      grouped[key].count += 1;
    });
    return Object.keys(grouped)
      .map((k) => grouped[k])
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }

  private titleCase(value: string): string {
    return String(value || '')
      .split(' ')
      .map((w) => (w ? `${w[0].toUpperCase()}${w.slice(1)}` : ''))
      .join(' ');
  }
}
